import React from 'react';
import PropTypes from 'prop-types';
import isUndefined from 'lodash/isUndefined';
import Carousel from '~shared/ui/Carousel';
import {
    getIsMobileWrapperFlag,
} from '~shared/utils/user';
import Tab from './Tab';
import { CAROUSEL_TABS_CSS_CLASS_NAME } from './constants';
import './styles/carousel-tabs.scss';

const propTypes = {
    /**
     * Array of items. One item for each tab.
     * Each item is an object with these properties:
     *     - {Function} [render] A function returning a React's Element that
     *           will render the tab's content.
     *     - Any property supported by Tab's options (see ./Tab.jsx).
     */
    items: PropTypes.arrayOf(PropTypes.object).isRequired,

    /**
     * Callback called when a tab is selected
     * @callback onTabSelected
     * @param {int} key The selected tab's key
     */
    onTabSelected: PropTypes.func,
    dateInfo: PropTypes.arrayOf(PropTypes.element),
    gtmAttribute: PropTypes.string,
    hasNavigation: PropTypes.bool,
    navigationOptions: PropTypes.instanceOf(Object),
};

/**
 * CarouselTabs
 *
 * A scrollable menu with tabs.
 */
class CarouselTabs extends React.Component {
    constructor(props) {
        super(props);

        this.content = null;
        this.state = {
            items: props.items,
            selectedTab: null,
            content: null,
            dateInfo: props.dateInfo,
        };

        this.onTabSelected = this.onTabSelected.bind(this);
        this.applyNavigation = this.applyNavigation.bind(this);
    }

    /**
     * When a tab is selected this callback is fired.
     * @param {Number} key The key of the selected tab
     * @param {Object} data Additional data
     * @return {undefined}
     */
    onTabSelected(key, data = null) {
        const items = this.state.items;
        items[this.state.selectedTab].selected = false;
        items[key].selected = true;

        this.setState({
            items,
            selectedTab: key,
        });

        if (!isUndefined(items[key].render)) {
            this.setState({
                content: items[key].render(),
            });
        }

        if (!isUndefined(this.props.onTabSelected)) {
            this.props.onTabSelected(key, data);
        }
    }

    /**
     * When the component mounts look for the selected tab in the state and
     * update the selectedTab state variable.
     *
     * @return {void}
     */
    componentDidMount() {
        this.state.items.forEach((item, key) => {
            if (item.selected) {
                this.setState({
                    selectedTab: key,
                });

                if (!isUndefined(item.render)) {
                    this.setState({
                        content: item.render(),
                    });
                }
            }
        });
    }

    applyNavigation(content) {
        const { hasNavigation, navigationOptions: options } = this.props;
        const isMobile = getIsMobileWrapperFlag();

        if(hasNavigation && !isMobile){
            return (
                <Carousel
                    ref={(ref) => {this.swiper=ref; }}
                    options={options}
                    customNav={true}
                    isMobile={isMobile}
                >
                    {content}
                </Carousel>
            );
        }

        return (
            <nav className={CAROUSEL_TABS_CSS_CLASS_NAME}>
                {content}
            </nav>
        );
    }

    /**
     * Render the tabs based on the items stored into the state.
     *
     * @return {ReactNode} The rendered component
     */
    render() {
        const { dateInfo } = this.state;

        const tabs = this.state.items.map((options, key) =>
            <Tab key={key}
                options={options}
                dateInfo={dateInfo && dateInfo[key] ? dateInfo[key] : null}
                onTabSelected={() => this.onTabSelected(key, options.data)}
                gtmAttribute={options.gtmAttribute ? options.gtmAttribute : this.props.gtmAttribute}
            />
        );

        return (
            <div>
                {this.applyNavigation(tabs)}
                <div>
                    {this.state.content}
                </div>
            </div>
        );
    }
}

CarouselTabs.propTypes = propTypes;

export default CarouselTabs;
